import { Trans } from '@lingui/react/macro';
import {
  ArrowBack,
  ArrowForward,
  CancelOutlined,
  CheckCircleOutline,
  ErrorOutline,
  InfoOutlined,
  TipsAndUpdatesOutlined,
} from '@mui/icons-material';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/videa-components/src/index';
import classNames from 'classnames';

import VideaBrandSm from '../../common-components/elements/icon/svgs/VideaBrandSm';

import { blueUltramarine400 } from '../../theme/videa-style-tokens';
import { ConfidenceDialog } from '../annotator/displayThreshold/ConfidenceDialog';
import { RBLColorSettings } from '../annotator/findingSidebar/findingList/rblColorSettings/RBLColorSettings';

import * as Styled from './styles';
import type {
  ContentIconProps,
  ContentImageProps,
  ContentParagraphProps,
  ContentSectionProps,
  ContentSectionsProps,
  ContentTitleProps,
  OnboardingContentProps,
  OnboardingFooterProps,
  OnboardingHeaderProps,
} from './types';

export const OnboardingHeader = (props: OnboardingHeaderProps) => {
  const { logo, navigateToApp } = props;
  return (
    <Styled.OnboardingHeader>
      <button
        className="close-button vai-bg-gray-200 vai-h-6 vai-w-6 vai-rounded vai-border-none vai-cursor-pointer hover:vai-bg-gray-400 vai-flex vai-items-center vai-justify-center"
        onClick={navigateToApp}
      >
        <FontAwesomeIcon
          icon={faTimes}
          className="vai-w-4 vai-h-4 vai-text-gray-600"
        />
      </button>
      {logo}
    </Styled.OnboardingHeader>
  );
};

export const OnboardingStart = () => {
  return (
    <Styled.OnboardingStart>
      <div className="intro">
        <VideaBrandSm color={blueUltramarine400} />
        <div className="vai-mt-6">
          <Trans>
            Step into the future of dentistry with VideaAI by VideaHealth! Your
            AI journey starts now by completing a quick three-minute setup
            process, ensuring a seamless blending of the VideaAI medical device
            into your daily routine. <br />
            <br />
            Get ready for success with helpful tips by clicking “Get Started.”
          </Trans>
        </div>
      </div>
    </Styled.OnboardingStart>
  );
};

export const OnboardingEnd = () => {
  return (
    <Styled.OnboardingEnd>
      <div className="outro">
        <p>
          <Trans>
            <TipsAndUpdatesOutlined />
            These tips can be accessed any time from the user menu or the{' '}
            <a
              href="https://help.videa.ai/"
              target="blank"
              className="inline-href"
            >
              Learning Center.
            </a>
          </Trans>
        </p>
        <p>
          <Trans>
            More training is available from VideaHealth in onboarding webinars.
            If you aren’t enrolled, please &nbsp;
            <a
              href="mailto:support@videa.ai?subject=webinar-enrollment"
              target="blank"
              className="inline-href"
            >
              get in touch
            </a>
            &nbsp; with us.
          </Trans>
        </p>
      </div>
    </Styled.OnboardingEnd>
  );
};

export const OnboardingFooter = (props: OnboardingFooterProps) => {
  const { handleBack, handleNext, isStart, isEnd, navigateToApp } = props;
  const nextButton = () => {
    if (isStart)
      return (
        <Button size="lg" className="vai-text-xl" onClick={handleNext}>
          Get Started
        </Button>
      );

    if (isEnd)
      return (
        <Button size="lg" className="vai-text-xl" onClick={navigateToApp}>
          Start VideaAI
        </Button>
      );

    return (
      <Button
        variant="ghost"
        className="vai-text-gray-500"
        onClick={handleNext}
      >
        Next
        <ArrowForward />
      </Button>
    );
  };
  return (
    <Styled.OnboardingFooter>
      <Button
        variant="ghost"
        className="vai-text-gray-500"
        disabled={isStart}
        onClick={handleBack}
      >
        <ArrowBack />
        Previous
      </Button>
      <Styled.Progress />
      {nextButton()}
    </Styled.OnboardingFooter>
  );
};

export const ContentTitle = (props: ContentTitleProps) => {
  const { title, subtitle, color } = props;
  const subtitleClasses = classNames('subtitle', {
    'title-default': color === 'default',
    'title-success': color === 'success',
    'title-warning': color === 'warning',
    'title-error': color === 'error',
  });
  return (
    <div className="title-header">
      <div className="title">{title}</div>
      {!!subtitle && <div className={subtitleClasses}>{subtitle}</div>}
    </div>
  );
};

export const ContentIcon = (props: ContentIconProps) => {
  const { icon } = props;
  switch (icon) {
    case 'check':
      return <CheckCircleOutline className="icon-header" color="success" />;
    case 'info':
      return <InfoOutlined className="icon-header" color="info" />;
    case 'warning':
      return <CancelOutlined className="icon-header" color="warning" />;
    case 'error':
      return <ErrorOutline className="icon-header" color="error" />;
    default:
      return null;
  }
};

export const ContentSection = (props: ContentSectionProps) => {
  const {
    section: { title, icon, color, images, paragraphs, embed },
  } = props;
  if (!paragraphs && !images) return null;
  const sectionIcon = !!icon && <ContentIcon colorIcon={color} icon={icon} />;
  const sectionHeaderClasses = classNames('section-header', {
    'section-header-icon': icon,
  });
  const embedComponent =
    !!embed && embed === 'rblSettings' ? (
      <RBLColorSettings isEmbedded />
    ) : (
      <ConfidenceDialog isEmbedded />
    );

  const sectionHeader = (
    <div className={sectionHeaderClasses}>
      {sectionIcon}
      {!!title && <div className={`title title-${color}`}>{title}</div>}
    </div>
  );

  const sectionEmbed = !!embed && embedComponent;
  const sectionParagraphs = !!paragraphs && paragraphs.length > 0 && (
    <div className="paragraphs">
      {paragraphs.map((paragraph: any, i: number) => {
        return <ContentParagraph key={`graph-${i}`} paragraph={paragraph} />;
      })}
    </div>
  );

  const sectionImages = !!images && images.length > 0 && (
    <div className="images">
      {images.map((image: any, i: number) => {
        return <ContentImage key={`image-${i}`} image={image} />;
      })}
    </div>
  );

  const sectionClasses = classNames('section', {
    'section-text': !sectionImages && sectionParagraphs,
    'section-images': sectionImages && !sectionParagraphs,
    'section-mix': sectionImages && sectionParagraphs,
    'section-embed': sectionEmbed,
  });

  return (
    <div className={sectionClasses}>
      {sectionHeader}
      {sectionImages}
      {sectionEmbed}
      {sectionParagraphs}
    </div>
  );
};

export const ContentParagraph = (props: ContentParagraphProps) => {
  const {
    paragraph: { text, title, icon, color, classLabel },
  } = props;
  const titleClasses = classNames('title', {
    'title-default': color === 'default',
    'title-success': color === 'success',
    'title-warning': color === 'warning',
    'title-error': color === 'error',
  });
  const paragraphIcon = !!icon && <ContentIcon colorIcon={color} icon={icon} />;
  const paragraphTitle = !!title && (
    <div className="paragraph-header">
      {paragraphIcon}
      <div className={titleClasses}>{title}</div>
    </div>
  );

  const paragraphClass = `paragraph-body ${classLabel}`;
  return (
    <div className="paragraph">
      {paragraphTitle}
      <div className={paragraphClass}>{text}</div>
    </div>
  );
};

export const ContentImage = (props: ContentImageProps) => {
  const {
    image: {
      imageUri,
      caption,
      captionTitle,
      alt: altText,
      format,
      scale = '2x',
    },
  } = props;
  const imageCaption =
    !!captionTitle ||
    (!!caption && (
      <div className="image-caption">
        {!!captionTitle && <div className="caption-title">{captionTitle}</div>}
        {!!caption && <div className="caption">{caption}</div>}
      </div>
    ));

  const imageClasses = classNames('content-image', {
    'image-full': format === 'block',
    'image-inline': format === 'inline-block',
  });
  return (
    <div className={imageClasses}>
      <img
        srcSet={`${imageUri} ${scale}`}
        alt={altText}
        className="image-element"
      />

      {imageCaption}
    </div>
  );
};

export const ContentSections = (props: ContentSectionsProps) => {
  const { sections } = props;
  const sectionsContent =
    !!sections &&
    sections.length > 0 &&
    sections.map((section: any, i: number) => {
      return <ContentSection key={`section-${i}`} section={section} />;
    });
  return <div className="sections">{sectionsContent}</div>;
};

export const OnboardingContent = (props: OnboardingContentProps) => {
  const { template, content } = props;
  const { title, subtitle, color, isEmbed, sections } = content;
  const contentClasses = classNames('template', {
    'template-intro': template === 'intro',
    'template-default': template === 'default',
    'template-embed': isEmbed,
  });
  return (
    <div className={contentClasses}>
      <ContentTitle title={title} subtitle={subtitle} color={color} />
      <ContentSections sections={sections} />
    </div>
  );
};
